import React from 'react';
import { Card, Row, Col } from 'react-bootstrap';
import { BloodworkAnalysis, PanelMarker, FurtherConsiderations, Section } from '../../models';
import { AnemiaStatus } from './bloodwork-anemia-status';
import { Footer } from '../../Footer';
import { TabsComponent } from '../tabs-component';
import { WBCEvaluation } from './bloodwork-wbc-evaluation';
import { GlucoseRegulationInsights } from './glucose-regulation-insights';
import { ThyroidRegulationInsights } from './thyroid-regulation-insights';
import { StressAdrenalRegulationInsights } from './stress-adrenal-regulation-insights';
import { DehydrationInsights } from './dehydration-insights';
import { adrenalGauge } from '../../models/StressAdrenalAnalysis';
import { BloodworkEntryStore } from '../../stores/bloodwork-entry-store';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

type Props = {
  section: Section;
  panelTitle: string;
  bloodworkAnalysis: BloodworkAnalysis;
  panelMarkers: PanelMarker[];
  furtherConsiderations: FurtherConsiderations;
  evalSmall: number;
  refSmall: number;
  evalLarge: number;
  refLarge: number;
  infoLink: string;
  bloodworkEntryStore: BloodworkEntryStore;
  enabledSupplementBrands: string[];
  collapsed: boolean;
  onToggle: () => void;
  includeInPdf: boolean;
  onIncludeInPdfToggle: () => void;
  // Optional extra checkboxes rendered in the header to the LEFT of the
  // "Include in PDF" checkbox (e.g. the Immune Activity section's quick
  // "Only Show Section Signals" / "Show Absolutes" view toggles). Listed
  // left-to-right in array order.
  extraToggles?: Array<{
    id: string;
    label: string;
    checked: boolean;
    onToggle: () => void;
    title?: string;
  }>;
};

export class BloodworkTestAnalysisSection extends React.Component<Props, {}> {

  evaluation = () => {
    const { bloodworkAnalysis, section, evalSmall, evalLarge } = this.props;
    if (section === Section.RBC) {
      return (
        <Col xs={evalSmall} lg={evalLarge}>
          <AnemiaStatus bloodworkAnalysis={bloodworkAnalysis} />
        </Col>  
      );
    } else if (section === Section.WBC && bloodworkAnalysis.wbc.hasValue()) {
      return (
        <Col xs={evalSmall} lg={evalLarge}>
          <WBCEvaluation bloodworkAnalysis={bloodworkAnalysis} />
        </Col>
      );
    } else if (section === Section.GLUCOSE) {
      // When the insights panel renders nothing (e.g. amylase is the only
      // glucose-section marker), skip the eval Col entirely so the Results
      // panel left-aligns instead of sitting behind an empty left gap.
      if (!GlucoseRegulationInsights.shouldRender(bloodworkAnalysis)) {
        return null;
      }
      return (
        <Col xs={evalSmall} lg={evalLarge}>
          <GlucoseRegulationInsights bloodworkAnalysis={bloodworkAnalysis} />
        </Col>
      );
    } else if (
      section === Section.ELECTROLYTES &&
      // Hidden in every shipped build. A REACT_APP_ENV deny-list alone is NOT
      // enough: only scripts/deploy.js sets that variable, so `npm run build`
      // / `build:prod` leave it undefined and `undefined !== 'prod'` reads as
      // "not prod" — which is why the Thyroid / Adrenal gates below would ship
      // their panels from an env-less build. The `!!` truthiness guard is what
      // makes this one fail closed: an unset value is falsy, so the panel
      // stays hidden no matter how the bundle was built.
      //
      // Kept inline rather than behind a helper so that in the slava / prod
      // builds — where CRA inlines REACT_APP_ENV as a literal — the whole
      // comparison folds to false and the panel is dropped from the bundle
      // entirely. (With REACT_APP_ENV unset there is nothing to fold, so the
      // code ships but never renders.) The NODE_ENV clause keeps `npm start`
      // working and folds away in any production build.
      (process.env.NODE_ENV !== 'production' ||
        (!!process.env.REACT_APP_ENV &&
          process.env.REACT_APP_ENV !== 'prod' &&
          process.env.REACT_APP_ENV !== 'slava'))
    ) {
      // Hydration gauge. Needs at least one of RBC/HGB/HCT to say anything, so
      // skip the eval Col entirely when there's no hemoconcentration panel —
      // same left-align behaviour as the Glucose and Adrenal sections.
      if (!DehydrationInsights.shouldRender(bloodworkAnalysis)) {
        return null;
      }
      return (
        <Col xs={evalSmall} lg={evalLarge}>
          <DehydrationInsights bloodworkAnalysis={bloodworkAnalysis} />
        </Col>
      );
    } else if (section === Section.THYROID && process.env.REACT_APP_ENV !== 'prod' && process.env.REACT_APP_ENV !== 'slava') {
      return (
        <Col xs={evalSmall} lg={evalLarge}>
          <ThyroidRegulationInsights bloodworkAnalysis={bloodworkAnalysis} />
        </Col>
      );
    } else if (section === Section.STRESS_ADRENAL && process.env.REACT_APP_ENV !== 'prod' && process.env.REACT_APP_ENV !== 'slava') {
      // With no Na/K ratio the insights gauge renders nothing (see
      // StressAdrenalRegulationInsights). Skip the eval Col entirely so the
      // Results panel left-aligns like sections without an insights panel,
      // instead of sitting in the right column behind an empty left gap.
      if (!adrenalGauge(bloodworkAnalysis.sodiumPotassiumRatio)) {
        return null;
      }
      return (
        <Col xs={evalSmall} lg={evalLarge}>
          <StressAdrenalRegulationInsights bloodworkAnalysis={bloodworkAnalysis} />
        </Col>
      );
    } else {
      return null;
    }
  }

  // `fullWidth` when this section has no left-hand insights/evaluation panel.
  // The tabs then take the whole card instead of stopping at refLarge (9/12) and
  // leaving the unused left column's width as dead space on the RIGHT — which
  // squeezed wide tab content (Notes, its digest cards, Root Causes) for no reason.
  analysis = (fullWidth: boolean) => {
    const { section, bloodworkAnalysis, panelMarkers, furtherConsiderations,
      refSmall, refLarge, bloodworkEntryStore, infoLink, enabledSupplementBrands } = this.props;
      return (
        <Col xs={fullWidth ? 12 : refSmall} lg={fullWidth ? 12 : refLarge}>
          <TabsComponent
            section={section}
            panelMarkers={panelMarkers}
            bloodworkAnalysis={bloodworkAnalysis} 
            furtherConsiderations={furtherConsiderations}
            bloodworkEntryStore={bloodworkEntryStore}
            infoLink={infoLink}
            enabledSupplementBrands={enabledSupplementBrands}
          />
        </Col>
      );
  }

  render() {
    const { panelTitle, panelMarkers, collapsed, onToggle, section, includeInPdf, onIncludeInPdfToggle, extraToggles } = this.props;
    const chevron = collapsed ? 'chevron-down' : 'chevron-up';
    // Built once so the tabs column knows whether the left column is there.
    const evaluationCol = this.evaluation();
    return (
      <>
      { panelMarkers.length > 0 ?
        <Card>
          <Card.Header
            onClick={onToggle}
            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
          >
            <span>
              <FontAwesomeIcon icon={chevron} />&nbsp;&nbsp;{panelTitle}
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 16 }}>
              {(extraToggles ?? []).map(toggle => (
                <label
                  key={toggle.id}
                  htmlFor={toggle.id}
                  onClick={e => e.stopPropagation()}
                  style={{ display: 'inline-flex', alignItems: 'center', margin: 0, fontWeight: 'normal', cursor: 'pointer', color: '#fff' }}
                  title={toggle.title}
                >
                  <span style={{ marginRight: '6px' }}>{toggle.label}</span>
                  <input
                    id={toggle.id}
                    type="checkbox"
                    className="lsm-styled-checkbox"
                    checked={toggle.checked}
                    onChange={toggle.onToggle}
                  />
                </label>
              ))}
              <label
                htmlFor={`include-in-pdf-${section}`}
                onClick={e => e.stopPropagation()}
                style={{ display: 'inline-flex', alignItems: 'center', margin: 0, fontWeight: 'normal', cursor: 'pointer', color: '#fff' }}
                title="Include this section in the Analysis PDF"
              >
                <span style={{ marginRight: '6px' }}>Include in PDF</span>
                <input
                  id={`include-in-pdf-${section}`}
                  type="checkbox"
                  className="lsm-styled-checkbox"
                  checked={includeInPdf}
                  onChange={onIncludeInPdfToggle}
                />
              </label>
            </span>
          </Card.Header>
          {!collapsed && (
            <>
              <Card.Body>
                <Row>
                  {evaluationCol}
                  {this.analysis(!evaluationCol)}
                </Row>
              </Card.Body>
              <Card.Footer>
                <Footer />
              </Card.Footer>
            </>
          )}
        </Card>
        : null
      }
      </>
    );
  }
}
